import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreBadge } from '@/entities/score-badge';
import { ApiError } from '@/shared/api/http';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { useDatasetScore } from '../api/scoring';
import { diagnose, formatRatio, shareOfDeficit } from '../model/presentation';

/* One dataset's score, as a panel another feature can mount.
 *
 * **Owned by `scoring` rather than assembled in `catalog-browser`**, which is the same placement
 * `ProfilePanel` has in `profiler` and for the same reason: the score's presentation rules — that
 * coverage always travels with the value, that `null` is never zero, that the decomposition reports
 * points *lost* rather than contributed — belong to the module that knows why. A dataset screen
 * composing `ScoreBadge`, `diagnose` and a contributions table itself would be a second place those
 * rules live, and the second place is the one that drifts.
 *
 * **The rule this exists to obey:** `docs/design/SCORING.md` — "a 98 on 4% coverage is a lie, and
 * shipping the score without coverage is the product telling that lie". So there is no branch here
 * that renders a value without its coverage.
 *
 * **A 404 is the ordinary answer and every other status is not.** A dataset no execution has covered
 * has no score, which is the state of most of a freshly harvested estate, so it gets an empty state
 * naming the next step. Anything else is reported as a failure — see the branch below for why that
 * distinction is not optional here.
 */

/** The one status that means "this dataset has no score", as opposed to "we could not read it". */
const NOT_FOUND = 404;

export interface DatasetScorePanelProps {
  readonly datasetId: string;
}

export function DatasetScorePanel({ datasetId }: DatasetScorePanelProps): ReactElement {
  const { t } = useTranslation();
  const score = useDatasetScore(datasetId);

  if (score.isPending) {
    return (
      <p role="status" className="lq-muted">
        {t('dataset.score.loading')}
      </p>
    );
  }

  /* **A 404 is "not scored yet"; anything else is a failure and says so.**
   *
   * The first version collapsed the two and called the cost accepted. It is not: a 500 here is
   * *reachable and was reached* within minutes of writing this. A stored score row carrying
   * `value = 90.0` with zero scored dimensions makes `Score.__post_init__` refuse to rehydrate — the
   * guard doing exactly its job — and the route answers 500. Rendering that as "not scored yet"
   * reports a platform fault as an ordinary state of the data, on the screen whose subject is the
   * score, and nobody would ever go looking.
   *
   * It is also the collapse this codebase records at four AI screens and twice in the profiler, so
   * committing it here while quoting those precedents would have been the fifth.
   *
   * A 404 still gets the empty state, because a dataset no execution has covered is the normal
   * condition of most of a freshly harvested estate and a red banner for it is how a reader learns
   * to ignore banners. */
  if (score.isError) {
    const notScored = score.error instanceof ApiError && score.error.status === NOT_FOUND;
    return notScored ? (
      <EmptyState
        title={t('dataset.score.noneTitle')}
        guidance={t('dataset.score.noneGuidance')}
      />
    ) : (
      <Alert tone="error" title={t('dataset.score.unreadableTitle')}>
        {score.error.message}
      </Alert>
    );
  }

  const finding = diagnose(score.data);
  const totalLost = score.data.contributions.reduce(
    (running, entry) => running + entry.points_lost,
    0,
  );

  return (
    <div className="lq-stack">
      <Card
        title={t('dataset.score.title')}
        hint={t('scoring.score.attribution', {
          version: score.data.score_model_version,
        })}
      >
        <div className="lq-toolbar">
          {/* The value and its coverage, together or not at all. */}
          <ScoreBadge
            score={score.data.value}
            coverage={formatRatio(score.data.coverage.column_ratio)}
          />
          <span className="lq-muted">
            {t('dataset.score.coverage', {
              covered: score.data.coverage.columns_with_a_rule,
              total: score.data.coverage.columns_total,
            })}
          </span>
        </div>

        {/* Evidence completeness, and it is a warning rather than a footnote: a score computed while
            some rules measured nothing is a real number over a smaller estate than the reader
            assumes, and that is the one thing about it worth interrupting for. */}
        {!score.data.evidence_complete ? (
          <Alert tone="warning" title={t('dataset.score.incompleteTitle')}>
            {t('dataset.score.incompleteBody')}
          </Alert>
        ) : null}

        {/* The costliest finding, in a sentence. Which dimension lost the most, and the single rule
            inside it that cost the most — see `diagnose`, where the per-dimension sum is what stops
            three small validity failures being outranked by one large completeness one. */}
        {finding !== null ? (
          <p className="lq-attention__blind">
            {t('scoring.decomposition.diagnosis', {
              dimension: t(`scoring.dimension.${finding.dimension}`),
              share: finding.share,
              rule: finding.ruleName,
              violations: finding.violationCount,
              rows: finding.rowsEvaluated,
            })}
          </p>
        ) : null}
      </Card>

      {score.data.contributions.length === 0 ? (
        /* A score with no contributions is a real state — every weighted dimension held evidence
           and nothing failed — and it is not the same as no score. Said rather than left as an
           empty table, which reads as a rendering fault. */
        <p className="lq-muted">{t('dataset.score.nothingLost')}</p>
      ) : (
        <Card flush>
          <DataTable
            caption={t('dataset.score.tableCaption')}
            columns={[
              { key: 'rule', label: t('scoring.decomposition.column.rule') },
              { key: 'dimension', label: t('scoring.decomposition.column.dimension') },
              { key: 'severity', label: t('scoring.decomposition.column.severity') },
              {
                key: 'pass',
                label: t('scoring.decomposition.column.pass'),
                numeric: true,
              },
              {
                key: 'lost',
                label: t('scoring.decomposition.column.lost'),
                numeric: true,
              },
            ]}
          >
            {score.data.contributions.map((entry) => {
              const share = shareOfDeficit(entry.points_lost, totalLost);
              return (
                <tr key={entry.rule_version_id}>
                  <td>
                    {/* The rule *version* label beside the name: two scores months apart under one
                        rule name are only comparable if the version behind each is on the record. */}
                    <PrimaryCell
                      secondary={`${entry.rule_label} · ${String(
                        entry.violation_count,
                      )}/${String(entry.rows_evaluated)}`}
                    >
                      {entry.rule_name}
                    </PrimaryCell>
                  </td>
                  <td>{t(`scoring.dimension.${entry.dimension}`)}</td>
                  <td>
                    <StatusBadge
                      tone="neutral"
                      label={t(`scoring.severity.${entry.severity}`)}
                    />
                  </td>
                  <td className="lq-mono">{Math.round(entry.pass_ratio * 100)}%</td>
                  {/* Points *lost*, not contributed. "What cost us" is the question a review asks,
                      and the sum of this column is exactly the deficit. */}
                  <td className="lq-mono">
                    {entry.points_lost.toFixed(2)}
                    {share !== null && <span className="lq-dim"> ({share}%)</span>}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Card>
      )}
    </div>
  );
}
